import { SetStateAction, Dispatch } from 'react';
import useCommonStore from 'store/useCommonStore';

type ModalPosition =
	| 'top'
	| 'bottom'
	| 'left'
	| 'right'
	| 'top_inner'
	| 'bottom_inner'
	| 'left_inner'
	| 'right_inner'
	| 'left_center'
	| 'top_center';

/** 모달의 위치를 셋팅 */
const useModal = (
	setModal: Dispatch<SetStateAction<boolean>> | ((value: boolean) => void),
): [
	(
		target: EventTarget & HTMLElement,
		modal_position_text: ModalPosition | ModalPosition[],
		figure?: number | number[],
	) => void,
	() => void,
] => {
	const { setModalPosition } = useCommonStore();

	const setPosition = (
		target: EventTarget & HTMLElement,
		modal_position_text: ModalPosition | ModalPosition[],
		figure?: number | number[], // margin
	) => {
		const _figure = figure ?? 0;

		const obj: Record<'top' | 'left' | 'bottom' | 'right', number | null> = {
			top: null,
			left: null,
			bottom: null,
			right: null,
		};

		// !! 이하 네이밍은 붙는 위치가 아닌 붙을 모달 창을 기준으로 정해진 것임에 유의

		// 타겟의 바로 아래에 붙을 때
		const _top = [
			target.offsetHeight + target.getBoundingClientRect().top,
			target.getBoundingClientRect().left,
		];
		if (modal_position_text === 'top') {
			obj.top =
				_top[0] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('top')]);
			obj.left = _top[1];
		} else if (modal_position_text.includes('top')) {
			obj.top =
				_top[0] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('top')]);
		}

		// 타겟의 윗면과 같은 위치에 붙을 때
		const _top_inner = [
			target.getBoundingClientRect().top,
			target.getBoundingClientRect().left,
		];
		if (modal_position_text === 'top_inner') {
			obj.top =
				_top_inner[0] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('top_inner')]);
			obj.left = _top_inner[1];
		} else if (modal_position_text.includes('top_inner')) {
			obj.top =
				_top_inner[0] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('top_inner')]);
		}

		// 타겟의 바로 위에 붙을 때
		const _bottom = [
			target.offsetHeight + (window.innerHeight - target.getBoundingClientRect().bottom),
			target.getBoundingClientRect().left,
		];

		if (modal_position_text === 'bottom') {
			obj.bottom =
				_bottom[0] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('bottom')]);
			obj.left = _bottom[1];
		} else if (modal_position_text.includes('bottom')) {
			obj.bottom =
				_bottom[0] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('bottom')]);
		}

		// 타겟의 아랫면과 같은 위치에 붙을 때
		const _bottom_inner = [
			window.innerHeight - target.getBoundingClientRect().bottom,
			target.getBoundingClientRect().left,
		];

		if (modal_position_text === 'bottom_inner') {
			obj.bottom =
				_bottom_inner[0] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('bottom_inner')]);
			obj.left = _bottom_inner[1];
		} else if (modal_position_text.includes('bottom_inner')) {
			obj.bottom =
				_bottom_inner[0] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('bottom_inner')]);
		}

		// 타겟의 바로 오른쪽에 붙을 때
		const _left = [
			target.getBoundingClientRect().top,
			target.offsetWidth + target.getBoundingClientRect().left,
		];

		if (modal_position_text === 'left') {
			obj.top = _left[0];
			obj.left =
				_left[1] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('left')]);
		} else if (modal_position_text.includes('left')) {
			obj.left =
				_left[1] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('left')]);
		}

		// 타겟의 왼쪽면과 같은 위치에 붙을 때
		const _left_inner = [
			target.getBoundingClientRect().top,
			target.getBoundingClientRect().left,
		];

		if (modal_position_text === 'left_inner') {
			obj.top = _left_inner[0];
			obj.left =
				_left_inner[1] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('left_inner')]);
		} else if (modal_position_text.includes('left_inner')) {
			obj.left =
				_left_inner[1] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('left_inner')]);
		}

		const _left_center = [
			target.getBoundingClientRect().top,
			target.getBoundingClientRect().left + target.offsetWidth / 2,
		];

		// center의 경우 Modal에 props로 x='-50%'를 추가로 넘겨줄 것!
		if (modal_position_text === 'left_center') {
			obj.top = _left_center[0];
			obj.left =
				_left_center[1] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('left_center')]);
		} else if (modal_position_text.includes('left_center')) {
			obj.left =
				_left_center[1] +
				(typeof _figure === 'number'
					? _figure
					: _figure[modal_position_text.indexOf('left_center')]);
		}

		setModalPosition(obj);
		setModal(true);
	};

	const resetPosition = () => {
		setModal(false);
		setModalPosition({ top: null, left: null, right: null, bottom: null });
	};

	return [setPosition, resetPosition];
};

export default useModal;
